import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  formatFinancialMoney,
  financialStatusLabel,
} from "./financialPresentation";
import styles from "./collections.module.scss";

const channelLabels = {
  email: "E-mail",
  whatsapp: "WhatsApp",
  voice: "Ligação",
};
const reasons = {
  NO_VALID_CONTACT:
    "Não há contato válido cadastrado no Asaas para este canal.",
  NO_SENDING_ACCOUNT:
    "Não há conta de envio definida para cobranças neste canal.",
  COLLECTION_SENDER_REVIEW_REQUIRED:
    "O remetente de cobrança ainda precisa ser validado.",
  SENDING_ACCOUNT_UNAVAILABLE:
    "A conta de WhatsApp precisa estar conectada e disponível.",
  SENDING_ACCOUNT_NUMBER_MISMATCH:
    "O número conectado não corresponde à conta definida. Confira a configuração.",
  SENDING_ACCOUNT_CHANGED: "A conta mudou. Atualize esta consulta.",
  EVOLUTION_DISPATCH_INTEGRATION_PENDING:
    "Conta conectada. A integração de envio ainda está em preparação.",
  CUSTOMER_CALL_INTEGRATION_PENDING:
    "As ligações de cobrança ainda não estão integradas. O teste interno está disponível em Ligações e custos.",
  CUSTOMER_DELETED: "O pagador está excluído no Asaas.",
  PAYMENT_DELETED: "A cobrança está excluída no Asaas.",
  PAYMENT_EXCEPTIONAL:
    "Há estorno ou contestação que precisa ser conferido no Asaas.",
  PAYMENT_NOT_OPEN:
    "A situação atual da cobrança não permite preparar uma cobrança em aberto.",
  NO_OPEN_VALUE: "Não há valor positivo confirmado para preparar a abordagem.",
  ASAAS_NOTIFICATIONS_DISABLED_REVIEW:
    "As notificações deste pagador estão desativadas no Asaas. Confira essa preferência antes de qualquer contato.",
  STATUS_DATE_REVIEW_REQUIRED:
    "A situação e o vencimento precisam ser conferidos no Asaas.",
};
export const validPreparation = (data, id) =>
  data?.source === "ASAAS" &&
  data.sourceEnvironment === "production" &&
  data.readOnly === true &&
  data.dispatchAllowed === false &&
  data.charge?.id === id &&
  /^\d{1,13}\.\d{2}$/.test(data.charge.amount || "") &&
  /^\d{4}-\d{2}-\d{2}$/.test(data.charge.dueDate || "") &&
  Number.isFinite(Date.parse(data.charge.dueDate)) &&
  new Date(data.charge.dueDate).toISOString().slice(0, 10) ===
    data.charge.dueDate &&
  typeof data.customer?.id === "string" &&
  typeof data.customer?.name === "string" &&
  Number.isFinite(Date.parse(data.expiresAt)) &&
  Date.parse(data.expiresAt) > Date.now() &&
  Number.isFinite(Date.parse(data.generatedAt)) &&
  Date.parse(data.generatedAt) <= Date.now() &&
  Date.parse(data.expiresAt) - Date.parse(data.generatedAt) <= 60000 &&
  data.review?.identityVerified === false &&
  data.review.contactOwnershipVerified === false &&
  ["BLOCKED", "PREPARATION_ONLY"].includes(data.review.state) &&
  Array.isArray(data.review.reasons) &&
  (data.review.state !== "BLOCKED" || data.review.reasons.length > 0) &&
  Array.isArray(data.customer.contacts) &&
  data.customer.contacts.every(
    (c) =>
      ["email", "phone", "mobilePhone"].includes(c.kind) &&
      ["MISSING", "INVALID", "REQUIRES_OWNERSHIP_REVIEW"].includes(c.state) &&
      (c.masked === null || typeof c.masked === "string")
  ) &&
  Array.isArray(data.channels) &&
  data.channels.length === 3 &&
  new Set(data.channels.map((c) => c.channel)).size === 3 &&
  data.channels.every(
    (c) =>
      channelLabels[c.channel] &&
      c.dispatchAllowed === false &&
      typeof c.accountReady === "boolean" &&
      typeof c.contactAvailable === "boolean" &&
      reasons[c.reason]
  ) &&
  (data.draft === null ||
    (data.review.state === "PREPARATION_ONLY" &&
      data.review.reasons.length === 0 &&
      data.draft.dispatchAllowed === false &&
      data.draft.containsFinancialData === false &&
      data.draft.saved === false &&
      typeof data.draft.text === "string"));

export default function CollectionPreparation({ paymentId, api, onClose }) {
  const [state, setState] = useState({ loading: true, data: null, error: "" });
  const [revision, setRevision] = useState(0);
  const close = useRef(null);
  const panel = useRef(null);
  useEffect(() => {
    const previousFocus = document.activeElement;
    if (close.current) close.current.focus();
    return () => {
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, []);
  useEffect(() => {
    let active = true;
    setState({ loading: true, data: null, error: "" });
    Promise.resolve()
      .then(() =>
        api.makeHttpRequest({
          method: "GET",
          sensitive: true,
          url: `/financial/collections/${paymentId}/preparation`,
        })
      )
      .then((data) => {
        if (!validPreparation(data, paymentId)) throw new Error();
        if (active) setState({ loading: false, data, error: "" });
      })
      .catch(() => {
        if (active)
          setState({
            loading: false,
            data: null,
            error:
              "Não foi possível conferir o cliente e a cobrança no Asaas. Atualize para tentar novamente.",
          });
      });
    return () => {
      active = false;
    };
  }, [api, paymentId, revision]);
  useEffect(() => {
    if (!state.data) return undefined;
    const timer = setTimeout(
      () =>
        setState({
          loading: false,
          data: null,
          error:
            "Esta prévia expirou. Atualize para conferir novamente a situação no Asaas.",
        }),
      Math.max(0, Date.parse(state.data.expiresAt) - Date.now())
    );
    return () => clearTimeout(timer);
  }, [state.data]);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
      if (e.key === "Tab") {
        const controls = Array.from(
          panel.current?.querySelectorAll(
            'button:not(:disabled), a[href], input:not(:disabled), [tabindex="0"]'
          ) || []
        );
        const first = controls[0],
          last = controls[controls.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          if (last) last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          if (first) first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  const data = state.data;
  return (
    <div
      className={styles.detailBackdrop}
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <aside
        ref={panel}
        className={styles.detailPanel}
        role="dialog"
        aria-modal="true"
        aria-label="Preparar contato de cobrança"
      >
        <header>
          <div>
            <span>Consulta atual no Asaas</span>
            <h2>Preparar contato</h2>
          </div>
          <button
            ref={close}
            type="button"
            onClick={onClose}
            aria-label="Fechar preparação"
          >
            ×
          </button>
        </header>
        <div className={styles.detailContent}>
          {state.loading && (
            <p role="status">Conferindo pagador e situação atual no Asaas…</p>
          )}
          {state.error && <p role="alert">{state.error}</p>}
          <button
            type="button"
            className="btn btn-secondary"
            disabled={state.loading}
            onClick={() => setRevision((v) => v + 1)}
          >
            Atualizar consulta
          </button>
          {data && (
            <>
              <section className={styles.detailHero}>
                <div>
                  <span>Pagador cadastrado no Asaas</span>
                  <strong>{data.customer.name}</strong>
                  <small>{data.charge.reference}</small>
                </div>
                <div>
                  <span>Valor da cobrança no Asaas</span>
                  <strong>{formatFinancialMoney(data.charge.amount)}</strong>
                  <small>
                    {financialStatusLabel(data.charge.status)} · vence{" "}
                    {data.charge.dueDate.split("-").reverse().join("/")}
                  </small>
                </div>
              </section>
              <section>
                <h3>Situação para preparação</h3>
                {data.review.state === "BLOCKED" ? (
                  <div role="status">
                    {data.review.reasons.map((reason) => (
                      <p key={reason}>
                        {reasons[reason] ||
                          "Esta situação precisa ser conferida pela equipe financeira."}
                      </p>
                    ))}
                  </div>
                ) : (
                  <p>
                    Dados consultados para conferência. O contato e a identidade
                    do destinatário ainda precisam ser verificados antes de
                    qualquer envio ou informação financeira.
                  </p>
                )}
                <p>
                  O valor acima pertence a esta cobrança. Ele não representa o
                  total de dívidas do pagador.
                </p>
              </section>
              <section>
                <h3>Contatos cadastrados no Asaas</h3>
                <div className={styles.auditList}>
                  {data.customer.contacts.map((c) => (
                    <div key={c.kind}>
                      <span>
                        {
                          {
                            email: "E-mail",
                            mobilePhone: "Celular",
                            phone: "Telefone",
                          }[c.kind]
                        }
                      </span>
                      <strong>
                        {c.masked ||
                          (c.state === "INVALID"
                            ? "Cadastro precisa de correção"
                            : "Não informado")}
                      </strong>
                      {c.state === "REQUIRES_OWNERSHIP_REVIEW" && (
                        <small>
                          Titularidade e autorização de contato ainda não
                          verificadas.
                        </small>
                      )}
                    </div>
                  ))}
                </div>
              </section>
              <section>
                <h3>Disponibilidade dos canais</h3>
                <div className={styles.auditList}>
                  {data.channels.map((c) => (
                    <div key={c.channel}>
                      <strong>
                        {channelLabels[c.channel]} · envio bloqueado
                      </strong>
                      <small>{reasons[c.reason]}</small>
                    </div>
                  ))}
                </div>
                <Link to="/admin/financeiro/ligacoes">
                  Testar o agente e consultar ligações e custos →
                </Link>
              </section>
              {data.draft && (
                <section>
                  <h3>Prévia de abordagem inicial</h3>
                  <p>{data.draft.text}</p>
                  <small>
                    Texto neutro para conferência. Não foi salvo como rascunho
                    nem enviado. A escolha do canal e a aprovação do contato
                    ainda estão pendentes.
                  </small>
                </section>
              )}
              <section>
                <h3>Histórico de contatos</h3>
                <p>
                  O histórico desta cobrança ainda não foi conciliado. Esta
                  consulta não confirma ausência de contatos anteriores.
                </p>
              </section>
              <footer>
                <strong>Preparação sem envio</strong>
                <span>
                  A consulta não altera o Asaas, não autoriza contato e não
                  inicia ligações ou mensagens.
                </span>
              </footer>
            </>
          )}
        </div>
      </aside>
    </div>
  );
}
